import { useEffect, useRef } from "react";

/**
 * La nuvola di gas e il fascio del laser sopra la pianta, portati da
 * `GasCloudOverlay.qml`.
 *
 * Due tele sovrapposte, e la separazione non e' un dettaglio di stile:
 *
 *  - il **fascio** (linea tratteggiata ciano + il punto verde
 *    dell'emettitore e quello rosso del bersaglio) sta su una tela sua a
 *    60 fps. Segue la testa PTZ, e a 15 fps i passi per fotogramma sono
 *    grossi abbastanza da farlo scattare — evidente sul raggio lungo.
 *  - la **nuvola** e' cara (rumore simplex su una griglia) e gira a 15 fps.
 *    Si disegna solo sopra i 150 ppm: sotto quella soglia non c'e' niente
 *    da far vedere, e una foschia perenne sulla pianta si legge come una
 *    perdita permanente.
 *
 * Il modello e' un pennacchio gaussiano attorno al segmento del fascio,
 * allargato sottovento; la scala di colore e' quella di una termocamera OGI.
 * Nessuno dei due numeri e' inventato in questo componente: `ppm` e il vento
 * arrivano dalla telemetria, gli estremi del fascio dalla posizione della
 * testa.
 *
 * **Una differenza voluta rispetto al `.qml`**: li' la funzione del rumore
 * legge `F2`, che non esiste (la proprieta' si chiama `_F2`), quindi ogni
 * campione solleva un ReferenceError e la nuvola non si forma. Qui la
 * costante e' quella giusta.
 */
export interface GasCloudProps {
  ppm: number;
  windSpeed: number;
  windDirection: number;
  /** Coordinate normalizzate 0-1 rispetto all'area della pianta. */
  sensorX: number;
  sensorY: number;
  laserEndX: number;
  laserEndY: number;
}

const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;
const GRAD2: [number, number][] = [
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

/** Permutazione del rumore: mulberry32 con seme 0, come nel `.qml` — la
 *  nuvola deve avere la stessa forma su ogni postazione. */
function buildPerm(): number[] {
  const p: number[] = [];
  for (let i = 0; i < 256; i++) p[i] = i;
  let s = 0;
  const rand = () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = p[i]!;
    p[i] = p[j]!;
    p[j] = tmp;
  }
  const perm = new Array<number>(512);
  for (let i = 0; i < 512; i++) perm[i] = p[i & 255]!;
  return perm;
}

/** Scala di colore OGI: caldo e morbido, senza picco bianco puro. */
function buildRamp() {
  const stops = [
    [0, 0, 0, 0, 0],
    [1, 210, 180, 40, 8],
    [64, 255, 210, 40, 130],
    [128, 255, 140, 30, 180],
    [192, 240, 60, 20, 210],
    [255, 255, 200, 120, 220],
  ];
  const r = new Array<number>(256);
  const g = new Array<number>(256);
  const b = new Array<number>(256);
  const a = new Array<number>(256);
  for (let i = 0; i < 256; i++) {
    let lo = 0;
    let hi = 1;
    for (let si = 1; si < stops.length; si++) {
      if (stops[si]![0]! >= i) {
        hi = si;
        lo = si - 1;
        break;
      }
    }
    const s0 = stops[lo]!;
    const s1 = stops[hi]!;
    const range = s1[0]! - s0[0]!;
    const t = range === 0 ? 0 : (i - s0[0]!) / range;
    r[i] = Math.round(s0[1]! + (s1[1]! - s0[1]!) * t);
    g[i] = Math.round(s0[2]! + (s1[2]! - s0[2]!) * t);
    b[i] = Math.round(s0[3]! + (s1[3]! - s0[3]!) * t);
    a[i] = Math.round(s0[4]! + (s1[4]! - s0[4]!) * t);
  }
  return { r, g, b, a };
}

function noise2(perm: number[], xin: number, yin: number): number {
  const sk = (xin + yin) * F2;
  const i = Math.floor(xin + sk);
  const j = Math.floor(yin + sk);
  const tt = (i + j) * G2;
  const x0 = xin - (i - tt);
  const y0 = yin - (j - tt);
  const i1 = x0 > y0 ? 1 : 0;
  const j1 = x0 > y0 ? 0 : 1;
  const x1 = x0 - i1 + G2;
  const y1 = y0 - j1 + G2;
  const x2 = x0 - 1 + 2 * G2;
  const y2 = y0 - 1 + 2 * G2;
  const ii = i & 255;
  const jj = j & 255;
  const g0 = GRAD2[perm[ii + perm[jj]!]! % 8]!;
  const g1 = GRAD2[perm[ii + i1 + perm[jj + j1]!]! % 8]!;
  const g2 = GRAD2[perm[ii + 1 + perm[jj + 1]!]! % 8]!;
  let t0 = 0.5 - x0 * x0 - y0 * y0;
  const n0 = t0 < 0 ? 0 : ((t0 *= t0) * t0 * (g0[0] * x0 + g0[1] * y0));
  let t1 = 0.5 - x1 * x1 - y1 * y1;
  const n1 = t1 < 0 ? 0 : ((t1 *= t1) * t1 * (g1[0] * x1 + g1[1] * y1));
  let t2 = 0.5 - x2 * x2 - y2 * y2;
  const n2 = t2 < 0 ? 0 : ((t2 *= t2) * t2 * (g2[0] * x2 + g2[1] * y2));
  return 70 * (n0 + n1 + n2); // [-1,1]
}

function fbm(perm: number[], x: number, y: number, timeOff: number): number {
  let v = 0;
  let amp = 1;
  let freq = 1;
  let maxAmp = 0;
  // 4 ottave: la struttura filamentosa viene da qui
  for (let oc = 0; oc < 4; oc++) {
    v += noise2(perm, x * freq + timeOff * 0.25, y * freq + timeOff * 0.1) * amp;
    maxAmp += amp;
    amp *= 0.5;
    freq *= 2;
  }
  return (v / maxAmp + 1) * 0.5; // [0,1]
}

/** Sotto i 150 ppm non si disegna niente: saturazione morbida sopra. */
function plumeVis(ppm: number): number {
  if (ppm <= 0) return 0;
  if (ppm < 150) return 0;
  const t = (ppm - 150) / 350;
  return Math.min(0.92, t / (1 + t * 0.6));
}

interface Beam {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}
interface Wind {
  dx: number;
  dy: number;
  s: number;
}

function plumeDensity(px: number, py: number, beam: Beam, wind: Wind, ppm: number): number {
  const vis = plumeVis(ppm);
  if (vis <= 0) return 0;
  // Distanza dal segmento del fascio
  const dx = beam.bx - beam.ax;
  const dy = beam.by - beam.ay;
  const lenSq = dx * dx + dy * dy;
  const t =
    lenSq < 1e-8
      ? 0
      : Math.max(0, Math.min(1, ((px - beam.ax) * dx + (py - beam.ay) * dy) / lenSq));
  const cx = beam.ax + t * dx;
  const cy = beam.ay + t * dy;
  const dist = Math.hypot(px - cx, py - cy);
  // Sottovento
  const ox = px - cx;
  const oy = py - cy;
  const downwind = ox * wind.dx + oy * wind.dy;
  const beamLen = Math.hypot(dx, dy);
  // Raggio di base contenuto: la nuvola sta attorno al punto di perdita, non
  // lungo tutto il fascio.
  const baseSigma = Math.max(1, beamLen * 0.07);
  const sigma =
    downwind > 0 ? baseSigma * (1 + wind.s * 0.08) : baseSigma * Math.max(0.5, 1 - wind.s * 0.03);
  const gaussian = Math.exp((-0.5 * dist * dist) / (sigma * sigma));
  const taper = Math.min(1, t * 3, (1 - t) * 3);
  return gaussian * taper * vis;
}

function windVec(speed: number, dir: number): Wind {
  const cs = Math.max(0.5, speed);
  const rad = ((dir + 180) % 360) * (Math.PI / 180);
  return { dx: Math.sin(rad), dy: -Math.cos(rad), s: cs };
}

/** Adatta la tela al riquadro e restituisce il contesto gia' scalato. */
function fit(canvas: HTMLCanvasElement): { ctx: CanvasRenderingContext2D; w: number; h: number } | undefined {
  const parent = canvas.parentElement;
  if (!parent) return undefined;
  const w = parent.clientWidth;
  const h = parent.clientHeight;
  if (w <= 0 || h <= 0) return undefined;
  const dpr = globalThis.devicePixelRatio || 1;
  const pw = Math.round(w * dpr);
  const ph = Math.round(h * dpr);
  if (canvas.width !== pw || canvas.height !== ph) {
    canvas.width = pw;
    canvas.height = ph;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return undefined;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { ctx, w, h };
}

export function GasCloudOverlay({ sample }: { sample: () => GasCloudProps }) {
  const cloudRef = useRef<HTMLCanvasElement | null>(null);
  const beamRef = useRef<HTMLCanvasElement | null>(null);
  // I valori si chiedono a ogni fotogramma, non arrivano come proprieta': la
  // posizione della testa e' interpolata a 60 fps e farla passare per lo stato
  // di React ridisegnerebbe l'intera schermata sessanta volte al secondo.
  const live = useRef(sample);
  live.current = sample;

  const noise = useRef<{ perm: number[]; ramp: ReturnType<typeof buildRamp> } | undefined>(
    undefined,
  );
  noise.current ??= { perm: buildPerm(), ramp: buildRamp() };

  // -- Fascio: 60 fps -------------------------------------------------
  useEffect(() => {
    const canvas = beamRef.current;
    if (!canvas) return;
    let raf = 0;
    let dash = 0;
    const frame = () => {
      const f = fit(canvas);
      if (f) {
        const { ctx, w, h } = f;
        const p = live.current();
        ctx.clearRect(0, 0, w, h);
        const ax = p.sensorX * w;
        const ay = p.sensorY * h;
        const bx = p.laserEndX * w;
        const by = p.laserEndY * h;
        ctx.save();
        ctx.strokeStyle = "rgba(0,255,255,0.5)";
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 10]);
        ctx.lineDashOffset = -(dash % 16);
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
        ctx.setLineDash([]);
        // Emettitore (verde)
        ctx.fillStyle = "rgba(34,197,94,0.8)";
        ctx.beginPath();
        ctx.arc(ax, ay, 3, 0, Math.PI * 2);
        ctx.fill();
        // Bersaglio (rosso)
        ctx.fillStyle = "rgba(239,68,68,0.8)";
        ctx.beginPath();
        ctx.arc(bx, by, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        dash += 0.48;
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  // -- Nuvola: 15 fps -------------------------------------------------
  useEffect(() => {
    const canvas = cloudRef.current;
    const n = noise.current;
    if (!canvas || !n) return;
    let timeOffset = 0;
    const tick = () => {
      const f = fit(canvas);
      if (!f) return;
      const { ctx, w, h } = f;
      const p = live.current();
      ctx.clearRect(0, 0, w, h);

      timeOffset += p.ppm > 150 ? 0.066 * p.windSpeed * 0.5 : 0;
      if (p.ppm <= 150) return;

      const beam: Beam = {
        ax: p.sensorX * w,
        ay: p.sensorY * h,
        bx: p.laserEndX * w,
        by: p.laserEndY * h,
      };
      const wind = windVec(p.windSpeed, p.windDirection);
      const { perm } = n;
      const { r: rampR, g: rampG, b: rampB, a: rampA } = n.ramp;

      const beamLen = Math.hypot(beam.bx - beam.ax, beam.by - beam.ay);
      const margin = Math.max(30, beamLen * 0.22);
      const minX = Math.max(0, Math.floor(Math.min(beam.ax, beam.bx) - margin));
      const maxX = Math.min(w - 1, Math.ceil(Math.max(beam.ax, beam.bx) + margin));
      const minY = Math.max(0, Math.floor(Math.min(beam.ay, beam.by) - margin));
      const maxY = Math.min(h - 1, Math.ceil(Math.max(beam.ay, beam.by) + margin));

      // Riferimento locale al fascio, per il rumore
      const bdx = beam.bx - beam.ax;
      const bdy = beam.by - beam.ay;
      const bLen = Math.hypot(bdx, bdy) || 1;
      const tx = bdx / bLen;
      const ty = bdy / bLen;
      const nx = -ty;
      const ny = tx;

      const STRIDE = 6;
      for (let py = minY; py <= maxY; py += STRIDE) {
        for (let px = minX; px <= maxX; px += STRIDE) {
          const density = plumeDensity(px + STRIDE / 2, py + STRIDE / 2, beam, wind, p.ppm);
          if (density < 0.003) continue;

          const relX = px - beam.ax;
          const relY = py - beam.ay;
          const along = relX * tx + relY * ty;
          const perp = relX * nx + relY * ny;
          const wisp = fbm(perm, along * 0.012, perp * 0.012, timeOffset * 0.6);
          const grain = fbm(perm, along * 0.06, perp * 0.06, timeOffset);

          // La densita' fa la forma, il rumore grande il contorno, quello
          // fine la grana: senza, la nuvola e' una pastiglia uniforme.
          const shaped = density * (0.15 + wisp * 1.2);
          const haloVal = Math.max(0, shaped) * (0.55 + grain * 0.45);

          let val = haloVal;
          if (p.ppm > 300 && density > 0.55 && wisp > 0.45) {
            const coreF = Math.min(1, Math.max(0, (density - 0.55) / 0.35));
            val = haloVal + coreF * (0.4 + grain * 0.4) * 0.3;
          }
          val = Math.min(0.78, val);

          const idx = Math.min(255, Math.max(0, Math.round(val * 255)));
          const a = rampA[idx]!;
          if (a < 4) continue;
          ctx.fillStyle = `rgba(${rampR[idx]!},${rampG[idx]!},${rampB[idx]!},${(a / 255).toFixed(3)})`;
          // Tre pixel di sovrapposizione: i riquadri si fondono invece di
          // formare una scacchiera.
          ctx.fillRect(px - 1, py - 1, STRIDE + 3, STRIDE + 3);
        }
      }
    };
    tick();
    const timer = setInterval(tick, 66);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <canvas className="gascloud" ref={cloudRef} />
      <canvas className="gascloud" ref={beamRef} />
    </>
  );
}
